import { useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Button, Grid, RegularBreakpoints, Stack } from '@mui/material';

import {
  KaseApi,
  MasterIndexApi,
  MasterIndexApiPath,
  MasterNameApi,
} from '@/api';
import {
  FileGrid,
  IViewCard,
  IViewItem,
  FormCard,
  ViewItem,
  PageHeader,
} from '@/components';
import NavigationButtonGroup from '@/components/NavigationButtonGroup/NavigationButtonGroup';
import { useKaseContext } from '@/hooks';
import { TabAnimationWrapper, useTabLayoutContext } from '@/layouts/TabLayout';
import { IMasterName } from '@/models';
import { MasterIndexDetailsContext } from '@/pages/MasterIndex/components/MasterIndexDetailsPage';
import MasterIndexRelatedRecords from '@/pages/MasterIndex/components/MasterIndexRelatedRecords/MasterIndexRelatedRecords';
import MasterIndexOrganizationNotes from '@/pages/MasterIndex/Organizations/Notes';
import { useToastStore } from '@/store';
import { flattenObject, generateUniqueId } from '@/utils';

import { useMasterNamesContext } from '..';
import {
  MasterOrganizationViewFields,
  TabNamesNextPaths,
  TabNamesScreens,
} from '../data';

interface TabCounts {
  relatedRecords: number;
  notes: number;
}

type MasterNameOrganizationViewProps = {
  hideHeader?: boolean;
  isMasterIndex?: boolean;
  onGoBack?: () => void;
};

export const MasterNameOrganizationView = ({
  hideHeader,
  isMasterIndex,
  ...props
}: MasterNameOrganizationViewProps) => {
  const outletContext = useOutletContext<
    MasterIndexDetailsContext | undefined
  >();
  const setCustomTitle = outletContext?.setCustomTitle;
  const setLastUpdatedAt = outletContext?.setLastUpdatedAt;
  const { updateToast } = useToastStore();
  const { currentMastername, setCurrentMastername, source } =
    useMasterNamesContext();
  const { isReadOnlyNames } = useKaseContext();
  const [currentName, setCurrentName] = useState<IMasterName>();
  const { updateCurrentScreen } = useTabLayoutContext();
  const [activeTab, setActiveTab] = useState('information');
  const [tabCounts, setTabCounts] = useState<TabCounts>({
    relatedRecords: 0,
    notes: 0,
  });

  const { id: organizationId } = useParams();

  const flattenNameObj = flattenObject({
    ...currentName,
  });

  const fetchTabCounts = useCallback(async (id: string) => {
    if (!id) return;

    try {
      const [relatedRes, notesRes] = await Promise.all([
        MasterIndexApi.getMasterRelatedRecords(
          MasterIndexApiPath.Organizations,
          id,
          { sort: JSON.stringify({ createdAt: -1 }) },
        ),
        MasterNameApi.getPaginatedMasterNameNotes(id, { limit: 1 }),
      ]);

      setTabCounts({
        relatedRecords:
          relatedRes.data?.totalDocs || relatedRes.data?.docs?.length || 0,
        notes: notesRes.data?.totalDocs || 0,
      });
    } catch {
      // Silently fail - counts are non-critical
    }
  }, []);

  useEffect(() => {
    if (currentMastername?.masterName._id) {
      fetchCurrentMastername();
      fetchTabCounts(currentMastername.masterName._id);
    }
  }, [currentMastername?.masterName._id]);

  useEffect(() => {
    if (organizationId) {
      fetchCurrentMastername();
      fetchTabCounts(organizationId);
    }
  }, [organizationId]);

  const fetchCurrentMastername = async () => {
    try {
      const res = organizationId
        ? await KaseApi.getMasterOrganization(organizationId)
        : await KaseApi.getMasterOrganization(
            String(currentMastername?.masterName._id),
          );
      setCurrentName(res.data);
      if (res.data.name && setCustomTitle) {
        setCustomTitle(
          res.data.uniqueId
            ? `${generateUniqueId('ORG', res.data.uniqueId)} - ${res.data.name}`
            : res.data.name,
        );
      }
      setLastUpdatedAt?.(res.data?.updatedAt);
      setCurrentMastername({
        masterName: res.data,
        relationship: {},
        kaseId: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    } catch (err: any) {
      updateToast({ open: true, message: err.msg });
    }
  };

  const onGoBack = () => {
    if (props.onGoBack) {
      return props.onGoBack();
    }
    updateCurrentScreen(TabNamesScreens.NamesList);
  };

  const handleEdit = () => {
    if (currentMastername) {
      setCurrentMastername(currentMastername);
      updateCurrentScreen(TabNamesScreens.MasterNameKaseOrganizationForm);
    }
  };

  const renderItems =
    (items: IViewItem[], size?: RegularBreakpoints) =>
    (row: Record<string, any>) =>
      items?.map((item: IViewItem) => {
        // Special handling for addresses field
        if (item.value === 'addresses') {
          const addresses = currentName?.addresses;

          if (!addresses || addresses.length === 0) {
            return (
              <ViewItem
                key={item.label}
                md={12}
                {...item}
                {...size}
                value=""
                copyable
              />
            );
          }

          // Pass the string to parser, not the array
          const value = item.parser
            ? item.parser(addresses as any)
            : JSON.stringify(addresses);

          return (
            <ViewItem
              key={item.label}
              md={12}
              {...item}
              {...size}
              value={value}
              copyable
              showMapsIcon={true}
            />
          );
        }

        // Default handling for other fields
        let value: any = item.value ? row[item.value as string] : row;
        if (item.parser) {
          value = item.parser(value);
        }

        return (
          <ViewItem
            key={item.label}
            md={12}
            {...item}
            {...size}
            value={value}
            copyable
          />
        );
      });

  const renderViewCardContent = ({
    key,
    items,
    size,
    multiple,
  }: IViewCard & { multiple?: string }) => {
    if (key === 'photos') {
      return (
        <ViewItem md={8}>
          <FileGrid files={flattenNameObj.files} readOnly={isReadOnlyNames} />
        </ViewItem>
      );
    }
    if (key === 'verificationStatus') {
      return (
        <ViewItem
          md={2}
          label="Verification Status"
          value={currentName?.isVerified ? 'Verified' : 'Not Verified'}
          copyable
        />
      );
    }
    const rowRenderer = renderItems(items, size);

    if (multiple) {
      const values = flattenNameObj[multiple];
      return values?.map((value: any, index: number) => (
        <Grid
          container
          item
          xs={12}
          columnSpacing={2}
          key={value._id || index}
          sx={{ '&:not(:last-of-type)': { mb: 1.5 } }}
        >
          {rowRenderer(value)}
        </Grid>
      ));
    }

    return rowRenderer(flattenNameObj);
  };

  const getTabs = () => {
    return [
      {
        label: 'Organization information',
        path: 'information',
      },
      {
        label: 'Related records',
        path: 'related',
        count: tabCounts.relatedRecords,
      },
      {
        label: 'Notes',
        path: 'notes',
        count: tabCounts.notes,
      },
    ];
  };

  const tabs = getTabs();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return (
          <Stack
            overflow="auto"
            sx={{
              '.p-kase-edit &': { p: 2.5, borderRadius: 2 },
            }}
          >
            {MasterOrganizationViewFields.map((data, index) => (
              <FormCard key={index} title={data.title} className="grey-title">
                {renderViewCardContent(data)}
              </FormCard>
            ))}
          </Stack>
        );

      case 'related':
        return (
          <MasterIndexRelatedRecords
            type={MasterIndexApiPath.Organizations}
            viewId={currentMastername?.masterName._id || organizationId}
          />
        );

      case 'notes':
        return (
          <MasterIndexOrganizationNotes
            viewId={currentMastername?.masterName._id || organizationId}
            readOnly={isReadOnlyNames}
            onCountChange={() =>
              fetchTabCounts(
                currentMastername?.masterName._id || organizationId || '',
              )
            }
          />
        );

      default:
        return null;
    }
  };

  const tabsWithHandlers = tabs.map((tab) => ({
    ...tab,
    onClick: () => setActiveTab(tab.path),
  }));

  const getOrganizationTitle = () => {
    if (currentName?.name) {
      if (currentName?.uniqueId && !isMasterIndex) {
        return `${generateUniqueId('ORG', currentName.uniqueId)} - ${
          currentName.name
        }`;
      }
      return currentName.name;
    }

    return undefined;
  };

  const renderActionButtons = () => {
    if (activeTab !== 'information') {
      return null;
    }

    const isKaseContext = source === 'kase';

    return (
      <Stack direction="row" spacing={2}>
        {!isMasterIndex && (
          <Button
            variant="outlined"
            onClick={() =>
              updateCurrentScreen(
                isKaseContext
                  ? TabNamesScreens.MasterNameKaseInvolvement
                  : TabNamesScreens.MasterNameSetInvolvement,
              )
            }
            sx={{
              color: 'text.primary',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'divider',
                backgroundColor: 'action.hover',
              },
            }}
          >
            Involvement
          </Button>
        )}
        {!isReadOnlyNames && (
          <Button color="error" variant="contained" onClick={handleEdit}>
            Edit
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <TabAnimationWrapper nextPaths={TabNamesNextPaths.nameList}>
      <div className="bg-[#F5F7F7] dark:bg-[#0B1922] flex flex-col h-full overflow-hidden rounded-lg">
        {!hideHeader && (
          <div className="flex-shrink-0 p-2">
            <PageHeader
              title={getOrganizationTitle()}
              onBack={onGoBack}
              action={!isMasterIndex ? renderActionButtons() : undefined}
            />
          </div>
        )}

        <div className="!w-full px-4">
          {!hideHeader && tabs && (
            <NavigationButtonGroup
              tabs={tabsWithHandlers}
              activeTab={activeTab}
            />
          )}
        </div>

        <div className="flex-1 overflow-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {renderTabContent()}
        </div>
      </div>
    </TabAnimationWrapper>
  );
};
